import { useState, useEffect, useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';

import { queryClient } from 'utils/react-query';
import {
  FeedbackSummaryJobResponse,
  useGetFeedbackSummaryJobStatus,
  useStartFeedbackSummaryJob,
  WorksheetFeedbackSummary,
  worksheetFeedbackSummaryQueryKey,
} from './useWorksheets';

const DEFAULT_PROGRESS_MESSAGE = 'Fortschritt wird geladen...';
const INITIAL_PROGRESS = 5;
const INITIAL_PROGRESS_MESSAGE = '🧠 Die KI analysiert die abgegebenen Antworten...';

type UseFeedbackSummaryGenerationResult = {
  generateSummary: (worksheetId: string, classId?: string) => void;
  isLoading: boolean;
  progress: number;
  progressMessage: string;
  error: string | null;
  reset: () => void;
};

const isCompletedSummary = (
  result: WorksheetFeedbackSummary | FeedbackSummaryJobResponse
): result is WorksheetFeedbackSummary => {
  return 'overallAssessment' in result;
};

export const useFeedbackSummaryGeneration = (
  worksheetId?: string,
  classId?: string
): UseFeedbackSummaryGenerationResult => {
  const [isPolling, setIsPolling] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  const storageKey = useMemo(
    () => (worksheetId ? `feedback-summary-job:${worksheetId}:${classId ?? 'all'}` : null),
    [worksheetId, classId]
  );

  const startMutation = useStartFeedbackSummaryJob();
  const statusMutation = useGetFeedbackSummaryJobStatus();

  const reset = useCallback(() => {
    setIsPolling(false);
    setProgress(0);
    setProgressMessage('');
    setError(null);
    setCurrentJobId(null);
    if (storageKey) {
      try {
        window.localStorage.removeItem(storageKey);
      } catch (storageError) {
        Sentry.captureException(storageError);
      }
    }
  }, [storageKey]);

  const handleCompletedSummary = useCallback(
    (summary: WorksheetFeedbackSummary) => {
      queryClient.setQueryData(
        worksheetFeedbackSummaryQueryKey(summary.worksheetId, summary.classId ?? undefined),
        summary
      );
      setIsPolling(false);
      setProgress(100);
      setProgressMessage('');
      setCurrentJobId(null);
      if (storageKey) {
        try {
          window.localStorage.removeItem(storageKey);
        } catch (storageError) {
          Sentry.captureException(storageError);
        }
      }
    },
    [storageKey]
  );

  const applyJobProgress = useCallback((result: FeedbackSummaryJobResponse) => {
    setProgress(result.progress);
    setProgressMessage(result.progressMessage || DEFAULT_PROGRESS_MESSAGE);
  }, []);

  const pollJobStatus = useCallback(
    async (jobId: string) => {
      if (statusMutation.isPending) return;

      try {
        const result = await statusMutation.mutateAsync(jobId);

        if (isCompletedSummary(result)) {
          handleCompletedSummary(result);
          return;
        }

        applyJobProgress(result);

        if (result.error) {
          setError(result.error);
          setIsPolling(false);
          setCurrentJobId(null);
          if (storageKey) {
            try {
              window.localStorage.removeItem(storageKey);
            } catch (storageError) {
              Sentry.captureException(storageError);
            }
          }
          return;
        }

        if (result.status === 'FAILED') {
          setError('Die KI-Zusammenfassung konnte nicht erstellt werden.');
          setIsPolling(false);
          setCurrentJobId(null);
          if (storageKey) {
            try {
              window.localStorage.removeItem(storageKey);
            } catch (storageError) {
              Sentry.captureException(storageError);
            }
          }
          return;
        }

        if (result.status === 'COMPLETED') {
          await queryClient.invalidateQueries({
            queryKey: worksheetFeedbackSummaryQueryKey(worksheetId, classId),
          });
          setError('Die Zusammenfassung wurde erstellt, konnte aber nicht geladen werden.');
          setIsPolling(false);
          setCurrentJobId(null);
          if (storageKey) {
            try {
              window.localStorage.removeItem(storageKey);
            } catch (storageError) {
              Sentry.captureException(storageError);
            }
          }
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Der Status der Zusammenfassung konnte nicht geladen werden.'
        );
        setIsPolling(false);
        setCurrentJobId(null);
        Sentry.captureException(err);
        if (storageKey) {
          try {
            window.localStorage.removeItem(storageKey);
          } catch (storageError) {
            Sentry.captureException(storageError);
          }
        }
      }
    },
    [statusMutation, storageKey, handleCompletedSummary, applyJobProgress, worksheetId, classId]
  );

  useEffect(() => {
    if (!isPolling || !currentJobId) return;

    const interval = setInterval(() => {
      pollJobStatus(currentJobId);
    }, 4000);

    return () => {
      clearInterval(interval);
    };
  }, [isPolling, currentJobId, pollJobStatus]);

  useEffect(() => {
    if (!isPolling || !currentJobId) return;
    pollJobStatus(currentJobId);
  }, [isPolling, currentJobId, pollJobStatus]);

  useEffect(() => {
    setIsPolling(false);
    setCurrentJobId(null);
    setError(null);
    setProgress(0);
    setProgressMessage('');

    if (!storageKey) return;

    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;

      const parsed: { jobId: string } = JSON.parse(raw);
      if (!parsed?.jobId) return;

      setCurrentJobId(parsed.jobId);
      setIsPolling(true);
      setProgress(INITIAL_PROGRESS);
      setProgressMessage(INITIAL_PROGRESS_MESSAGE);
    } catch (storageError) {
      Sentry.captureException(storageError);
    }
  }, [storageKey]);

  const generateSummary = useCallback(
    async (targetWorksheetId: string, targetClassId?: string) => {
      reset();
      setProgress(INITIAL_PROGRESS);
      setProgressMessage(INITIAL_PROGRESS_MESSAGE);

      try {
        const result = await startMutation.mutateAsync({
          worksheetId: targetWorksheetId,
          classId: targetClassId,
        });

        setCurrentJobId(result.jobId);
        setIsPolling(true);
        applyJobProgress(result);

        const key = `feedback-summary-job:${targetWorksheetId}:${targetClassId ?? 'all'}`;
        try {
          window.localStorage.setItem(
            key,
            JSON.stringify({ jobId: result.jobId, startedAt: Date.now() })
          );
        } catch (storageError) {
          Sentry.captureException(storageError);
        }

        if (result.status === 'FAILED') {
          setError(result.error || 'Die KI-Zusammenfassung konnte nicht erstellt werden.');
          setIsPolling(false);
          setCurrentJobId(null);
          try {
            window.localStorage.removeItem(key);
          } catch (storageError) {
            Sentry.captureException(storageError);
          }
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Die KI-Zusammenfassung konnte nicht gestartet werden.'
        );
        Sentry.captureException(err);
      }
    },
    [reset, startMutation, applyJobProgress]
  );

  return {
    generateSummary,
    isLoading: startMutation.isPending || isPolling,
    progress,
    progressMessage,
    error,
    reset,
  };
};
